import type { KotsQuery, OrdersQuery } from '../types/dine.types';

/*
 * DineOps endpoints — docs/api-contract.md. Every path is under /api/v1/dine.
 * No endpoint takes a dineOutletId: the outlet is resolved server-side.
 */

const BASE = '/api/v1/dine';

function query(params: Record<string, string | number | boolean | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const dineEndpoints = {
  outlet: () => `${BASE}/outlet`,

  // Buffet check-off (slice 2): today's buffet meals, a meal's board, marks, undo, close.
  buffetToday: () => `${BASE}/buffet/today`,
  buffetBoard: (meal: string) => `${BASE}/buffet/${encodeURIComponent(meal)}`,
  buffetMarks: (meal: string) => `${BASE}/buffet/${encodeURIComponent(meal)}/marks`,
  buffetUndo: (markId: string) => `${BASE}/buffet/marks/${markId}/undo`,
  buffetClose: (meal: string) => `${BASE}/buffet/${encodeURIComponent(meal)}/close`,
  // Buffet slice 3: billing guests outside the plan.
  buffetBillPreview: (meal: string) => `${BASE}/buffet/${encodeURIComponent(meal)}/bills/preview`,
  buffetBills: (meal: string) => `${BASE}/buffet/${encodeURIComponent(meal)}/bills`,
  myOutlets: () => `${BASE}/outlets/mine`,
  card: (orderId?: string) => `${BASE}/menu/card${orderId ? `?orderId=${encodeURIComponent(orderId)}` : ''}`,

  tables: (pageSize = 100) => `${BASE}/tables${query({ pageSize })}`,
  sections: (pageSize = 100) => `${BASE}/sections${query({ pageSize })}`,
  table: (id: string) => `${BASE}/tables/${id}`,
  tableClear: (id: string) => `${BASE}/tables/${id}/clear`,

  orders: (q: OrdersQuery = {}) =>
    `${BASE}/orders${query({ status: q.status, sort: q.sort, mine: q.mine })}`,
  order: (id: string) => `${BASE}/orders/${id}`,
  orderCovers: (id: string) => `${BASE}/orders/${id}/covers`,
  orderSeating: (id: string) => `${BASE}/orders/${id}/seating`,
  orderAvoid: (id: string) => `${BASE}/orders/${id}/avoid`,
  orderLines: (id: string) => `${BASE}/orders/${id}/lines`,
  orderLine: (id: string, lineId: string) => `${BASE}/orders/${id}/lines/${lineId}`,
  orderOpenItem: (id: string) => `${BASE}/orders/${id}/lines/open-item`,
  orderLineApprove: (id: string, lineId: string) =>
    `${BASE}/orders/${id}/lines/${lineId}/approve`,
  orderCharge: (id: string) => `${BASE}/orders/${id}/lines/charge`,
  orderLineCancel: (id: string, lineId: string) => `${BASE}/orders/${id}/lines/${lineId}/cancel`,
  // Return dish (2026-10-23).
  orderLineReturn: (id: string, lineId: string) => `${BASE}/orders/${id}/lines/${lineId}/return`,
  orderReturnApprove: (id: string, returnId: string) => `${BASE}/orders/${id}/returns/${returnId}/approve`,
  orderReturnReject: (id: string, returnId: string) => `${BASE}/orders/${id}/returns/${returnId}/reject`,
  approvals: () => `${BASE}/orders/approvals`,
  // Requisitions an approval rule held, for a holder of the routed role (S3b).
  requisitionsAwaitingApproval: () => `${BASE}/requisitions/awaiting-my-approval`,
  requisitionApprove: (id: string) => `${BASE}/requisitions/${id}/approve`,
  requisitionReject: (id: string) => `${BASE}/requisitions/${id}/reject`,
  orderHold: (id: string) => `${BASE}/orders/${id}/hold`,
  orderCancel: (id: string) => `${BASE}/orders/${id}/cancel`,
  orderMove: (id: string) => `${BASE}/orders/${id}/move`,
  orderMerge: (id: string) => `${BASE}/orders/${id}/merge`,
  orderSend: (id: string) => `${BASE}/orders/${id}/send`,
  orderRequestBill: (id: string) => `${BASE}/orders/${id}/request-bill`,
  /** Interim until cycle 4 settlement (docs/decisions.md #4). */
  orderClose: (id: string) => `${BASE}/orders/${id}/close`,

  kots: (q: KotsQuery = {}) =>
    `${BASE}/kots${query({ station: q.station, status: q.status, mine: q.mine })}`,
  kot: (id: string) => `${BASE}/kots/${id}`,
  kotAccept: (id: string) => `${BASE}/kots/${id}/accept`,
  kotReady: (id: string) => `${BASE}/kots/${id}/ready`,
  kotLineReady: (id: string, lineId: string) => `${BASE}/kots/${id}/lines/${lineId}/ready`,
  kotServe: (id: string) => `${BASE}/kots/${id}/serve`,
  kotReject: (id: string) => `${BASE}/kots/${id}/reject`,
  kotPrint: (id: string) => `${BASE}/kots/${id}/print`,
  guestsInHouse: (q?: string) => `${BASE}/guests/in-house${q ? `?q=${encodeURIComponent(q)}` : ''}`,
};
